import React, { useEffect, useState } from 'react';
import { X, Truck, PackageCheck, Loader2 } from 'lucide-react';
import { requisitionsApi } from '../api';

/** Single "Mark this requisition" modal used by Warehouse (Fulfilled),
 *  Logistics (Shipped / Delivered) and SA/Admin. Captures ONE courier +
 *  ONE tracking number for the whole requisition — matches the operating
 *  reality of one AWB per parcel.
 *
 *  Props:
 *    open        — bool
 *    onClose     — fn()
 *    reqId       — string
 *    initialStatus — 'Fulfilled' | 'Shipped' | 'Delivered'  (defaults from caller's role)
 *    initialCourier / initialTracking — prefill (Logistics editing what Warehouse set)
 *    onDone(newReq)  — fired after a successful call with the fresh requisition
 *    userRole    — used to gate which statuses are offered
 */
const STATUS_OPTIONS_BY_ROLE = {
  // Warehouse can process (Fulfilled) and optionally capture the first AWB.
  Warehouse: [{ value: 'Fulfilled', label: 'Mark Fulfilled', icon: PackageCheck }],
  // Logistics can move to Shipped or Delivered.
  Logistics: [
    { value: 'Shipped', label: 'Mark Shipped', icon: Truck },
    { value: 'Delivered', label: 'Mark Delivered', icon: PackageCheck },
  ],
  Admin: [
    { value: 'Fulfilled', label: 'Mark Fulfilled', icon: PackageCheck },
    { value: 'Shipped', label: 'Mark Shipped', icon: Truck },
    { value: 'Delivered', label: 'Mark Delivered', icon: PackageCheck },
  ],
  SuperAdmin: [
    { value: 'Fulfilled', label: 'Mark Fulfilled', icon: PackageCheck },
    { value: 'Shipped', label: 'Mark Shipped', icon: Truck },
    { value: 'Delivered', label: 'Mark Delivered', icon: PackageCheck },
  ],
  AllIndiaManager: [
    { value: 'Fulfilled', label: 'Mark Fulfilled', icon: PackageCheck },
    { value: 'Shipped', label: 'Mark Shipped', icon: Truck },
    { value: 'Delivered', label: 'Mark Delivered', icon: PackageCheck },
  ],
};

const OTHER_COURIER = 'Other';

export default function ShipMarkModal({ open, onClose, reqId, initialStatus, initialCourier = '', initialTracking = '', onDone, userRole }) {
  const options = STATUS_OPTIONS_BY_ROLE[userRole] || [];
  const [status, setStatus] = useState(initialStatus || options[0]?.value || 'Fulfilled');
  const [courier, setCourier] = useState('');
  const [courierCustom, setCourierCustom] = useState('');
  const [tracking, setTracking] = useState(initialTracking || '');
  const [courierList, setCourierList] = useState([]);
  const [trackingDigits, setTrackingDigits] = useState({ min: 6, max: 30 });
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset local state when the modal is (re-)opened.
  useEffect(() => {
    if (!open) return;
    setErr('');
    setStatus(initialStatus || options[0]?.value || 'Fulfilled');
    setTracking(initialTracking || '');
    // Match the prefilled courier against the whitelist so the dropdown
    // shows the right selection; else fall back to "Other" + custom text.
    if (courierList.length > 0) {
      if (initialCourier && courierList.includes(initialCourier)) {
        setCourier(initialCourier);
        setCourierCustom('');
      } else if (initialCourier) {
        setCourier(OTHER_COURIER);
        setCourierCustom(initialCourier);
      } else {
        setCourier('');
        setCourierCustom('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCourier, initialTracking, initialStatus, courierList]);

  // Fetch the courier whitelist once per mount — small payload, no auth issue.
  useEffect(() => {
    let cancel = false;
    requisitionsApi.couriers()
      .then(cfg => { if (!cancel) { setCourierList(cfg.options || []); setTrackingDigits({ min: cfg.trackingMinDigits || 6, max: cfg.trackingMaxDigits || 30 }); } })
      .catch(() => { /* keep an empty list — free-text fallback still works */ });
    return () => { cancel = true; };
  }, []);

  if (!open) return null;

  const resolvedCourier = courier === OTHER_COURIER ? courierCustom.trim() : courier;
  const trackingClean = tracking.replace(/\s+/g, '');
  const bothOrNeither = (!resolvedCourier && !trackingClean) || (resolvedCourier && trackingClean);
  const trackingValid = !trackingClean || (/^\d+$/.test(trackingClean) && trackingClean.length >= trackingDigits.min && trackingClean.length <= trackingDigits.max);
  const canSubmit = !!status && bothOrNeither && trackingValid && !submitting;

  const submit = async () => {
    setErr(''); setSubmitting(true);
    try {
      const payload = { lineStatus: status };
      if (resolvedCourier && trackingClean) {
        payload.courier = resolvedCourier;
        payload.tracking = trackingClean;
      }
      const updated = await requisitionsApi.markAll(reqId, payload);
      onDone?.(updated);
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to mark requisition');
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ship-mark-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="fm-card w-full max-w-[520px] bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E2D3]">
          <div className="flex items-center gap-2 text-[15px] font-semibold">
            <Truck size={16} /> Mark requisition
          </div>
          <button onClick={onClose} className="text-[#7a6f5c] hover:text-[#232323]" data-testid="ship-mark-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-[#3d3427] mb-1.5">Action</label>
            <div className="flex gap-2 flex-wrap">
              {options.map(o => {
                const Icon = o.icon;
                const active = status === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`ship-mark-status-${o.value}`}
                    onClick={() => setStatus(o.value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] border transition-colors ${
                      active
                        ? 'bg-[#232323] text-white border-[#232323]'
                        : 'bg-white text-[#3d3427] border-[#E7E2D3] hover:bg-[#F7F4EA]'
                    }`}
                  >
                    <Icon size={13} /> {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-[#3d3427] mb-1.5">
              Courier <span className="text-[#7a6f5c] font-normal">(optional — capture together with tracking)</span>
            </label>
            <select
              data-testid="ship-mark-courier"
              value={courier}
              onChange={e => setCourier(e.target.value)}
              className="fm-select fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
            >
              <option value="">— select courier —</option>
              {courierList.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {courier === OTHER_COURIER && (
              <input
                data-testid="ship-mark-courier-other"
                type="text"
                value={courierCustom}
                onChange={e => setCourierCustom(e.target.value)}
                placeholder="Enter courier name"
                maxLength={60}
                className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white mt-2"
              />
            )}
          </div>

          <div>
            <label htmlFor="ship-mark-tracking" className="block text-[12px] font-medium text-[#3d3427] mb-1.5">
              Tracking number
              <span className="text-[#7a6f5c] font-normal"> (digits only · {trackingDigits.min}–{trackingDigits.max} chars)</span>
            </label>
            <input
              id="ship-mark-tracking"
              data-testid="ship-mark-tracking"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={tracking}
              // Enforce digits-only client-side so paste of "BLUE-1234" strips to "1234".
              onChange={e => setTracking(e.target.value.replace(/\D+/g, ''))}
              placeholder="e.g. 1234567890"
              maxLength={trackingDigits.max}
              className={`fm-focus w-full border rounded-md px-3 py-2 text-[13.5px] bg-white font-mono ${
                tracking && !trackingValid ? 'border-[#B84A4A]' : 'border-[#E7E2D3]'
              }`}
            />
            {tracking && !trackingValid && (
              <div className="text-[11px] text-[#B84A4A] mt-1">
                Tracking must be {trackingDigits.min}–{trackingDigits.max} digits.
              </div>
            )}
          </div>

          <div className="text-[11.5px] text-[#7a6f5c] bg-[#F7F4EA] p-2 rounded">
            The same courier and tracking will be stamped on <span className="font-semibold text-[#3d3427]">every line</span> of this requisition — one AWB per parcel.
          </div>

          {err && <div className="text-[12.5px] text-[#B84A4A]" data-testid="ship-mark-err">{err}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E7E2D3] bg-[#FAF7EE]">
          <button
            onClick={onClose}
            data-testid="ship-mark-cancel"
            className="px-3 py-1.5 rounded-md text-[13px] border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="ship-mark-submit"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-white text-[13px] font-medium disabled:opacity-50"
            style={{ background: canSubmit ? '#232323' : '#7a6f5c' }}
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : null}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
